import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { StepNavigation } from '@/components/commercial/accounts/create/step-navigation';
import { StepConfigurations } from '@/components/commercial/proposals/create/configurations/step-configurations';
import { StepCart } from '@/components/commercial/proposals/create/step-cart';
import {
  StepCartFooterActions,
  StepCartHeaderActions,
} from '@/components/commercial/proposals/create/step-cart-actions';
import { StepDelivery } from '@/components/commercial/proposals/create/step-delivery';
import { StepClientUpdateContainer } from '@/components/commercial/proposals/update/step-client-container';
import { StepCreditAnalysisUpdate } from '@/components/commercial/proposals/update/step-credit-analysis';
import { UpdateProposalProvider, useUpdateProposal } from '@/context/update-proposal-context';
import { useProposalConfigurations } from '@/hooks/commercial/use-proposal-configurations';
import { useProposalStepProgress } from '@/hooks/use-proposal-step-progress';
import { toast } from '@/hooks/use-toast';

const STEP_IDS = ['client', 'credit-analysis', 'cart', 'config', 'delivery', 'resume'] as const;

function ProposalUpdateContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = searchParams.get('step') || 'client';

  const {
    data: { proposal, account },
    isLoading,
    error,
  } = useUpdateProposal();

  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<string | null>(null);
  const [cartValidation, setCartValidation] = useState<{
    isValid: boolean;
    message: string | null;
  }>({ isValid: true, message: null });
  const [isSaving, setIsSaving] = useState(false);

  const savePaymentFnRef = useRef<(() => Promise<void>) | null>(null);
  const isCartStep = currentStep === 'cart';

  const deliveryAddressId = selectedDeliveryAddressId ?? proposal?.deliveryAddressId ?? null;
  const proposalId = proposal?.id;
  const { data: proposalConfigurations } = useProposalConfigurations(proposalId);
  const isConfigurationStepValid = proposalConfigurations?.isValidated ?? false;

  const steps = useProposalStepProgress({
    proposal,
    deliveryAddressId,
    isCartStepValid: cartValidation.isValid,
    isConfigurationStepValid,
  });

  useEffect(() => {
    if (error) {
      navigate('/commercial/proposal');
    }
  }, [error, navigate]);

  const handleSavePaymentRef = useCallback((saveFn: () => Promise<void>) => {
    savePaymentFnRef.current = saveFn;
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await savePaymentFnRef.current?.();
      if (proposalId) {
        await queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] });
        await queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
        await queryClient.invalidateQueries({ queryKey: ['proposal-configurations', proposalId] });
      }
      toast({ title: 'Proposta salva com sucesso' });
    } catch {
      toast({ title: 'Erro ao salvar proposta', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  }, [proposalId, queryClient]);

  const handleNextStep = useCallback(async () => {
    setIsSaving(true);
    try {
      await savePaymentFnRef.current?.();
      if (proposalId) {
        await queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] });
        await queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
        await queryClient.invalidateQueries({ queryKey: ['proposal-configurations', proposalId] });
      }
      const currentIdx = STEP_IDS.indexOf(currentStep as (typeof STEP_IDS)[number]);
      const nextStep = STEP_IDS[currentIdx + 1];
      if (nextStep) {
        setSearchParams({ step: nextStep }, { replace: true });
      }
    } catch {
      toast({ title: 'Erro ao salvar proposta', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  }, [currentStep, proposalId, queryClient, setSearchParams]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // V-FE-04: Page title should reflect "Proposta [ID] – [Razão Social]"
  const pageTitle = proposal
    ? `Proposta ${proposal.seq} - ${proposal.clientName || account?.social_name || account?.name || 'Cliente'}`
    : 'Editar Proposta';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{pageTitle}</h1>
          <p className="text-muted-foreground">Edite os dados da proposta comercial</p>
        </div>
        {isCartStep && (
          <StepCartHeaderActions
            onCancel={() => navigate('/commercial/proposal')}
            onSave={handleSave}
            isSaving={isSaving}
          />
        )}
      </div>

      {/* Step Navigation */}
      <div className="mb-6">
        <StepNavigation steps={steps} />
      </div>

      {/* Step Content */}
      <div className="w-full max-w-[1400px] mx-auto">
        {currentStep === 'client' && <StepClientUpdateContainer proposalId={proposal?.id || ''} />}
        {currentStep === 'credit-analysis' && (
          <StepCreditAnalysisUpdate proposalId={proposal?.id || ''} />
        )}
        {isCartStep && (
          <>
            <StepCart
              proposalId={proposal?.id}
              clientId={proposal?.clientId}
              savedPayment={proposal?.payment}
              onValidationChange={(isValid, message) => setCartValidation({ isValid, message })}
              onSavePaymentRef={handleSavePaymentRef}
            />
            <StepCartFooterActions
              onNextStep={handleNextStep}
              isStepValid={cartValidation.isValid}
              validationHint={cartValidation.message}
              isSaving={isSaving}
            />
          </>
        )}
        {currentStep === 'config' && (
          <>
            <StepConfigurations proposalId={proposal?.id} />
            <StepCartFooterActions
              onNextStep={handleNextStep}
              isStepValid={isConfigurationStepValid}
              validationHint={
                isConfigurationStepValid ? null : 'Configure todos os rastreadores para avançar'
              }
              isSaving={isSaving}
            />
          </>
        )}
        {currentStep === 'delivery' && (
          <StepDelivery
            accountId={proposal?.clientId}
            proposalId={proposal?.id}
            selectedAddressId={selectedDeliveryAddressId}
            onSelectAddress={setSelectedDeliveryAddressId}
          />
        )}
        {currentStep === 'resume' && (
          <div className="text-center py-12 text-muted-foreground">Resumo - Em desenvolvimento</div>
        )}
      </div>
    </motion.div>
  );
}

export function ProposalUpdatePage() {
  const params = useParams();
  const proposalId = params.id;

  if (!proposalId) {
    return null;
  }

  return (
    <UpdateProposalProvider proposalId={proposalId}>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        }
      >
        <ProposalUpdateContent />
      </Suspense>
    </UpdateProposalProvider>
  );
}
